import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import {
    Input,
} from '@connected/ui/components/input';
import { Page, Section } from '@connected/ui/features/layouts';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FinalCircuitCreateModal, FinalCircuitTable } from '@/components/features/final-circuit-list';
import { Loading } from '@/components/loading';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useUrlSearchSync } from '@/lib/utils';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/final-circuits/',
)({
    component: FinalCircuitPage,
    validateSearch: z.object({
        search: z.string().optional(),
    }),
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({
            electricalProjectVersionId: params.projectVersionId,
        }));
    },
    pendingComponent: () => <Loading action="Achterliggende installaties aan het laden" />,
});

function FinalCircuitPage() {
    const { t } = useTranslation('re-inspections');

    const params = Route.useParams();
    const search = Route.useSearch();
    const navigate = useNavigate();
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [searchField, setSearchField] = useUrlSearchSync({
        search: search.search,
        navigate: (newSearch) => {
            navigate({
                to: '.',
                search: prev => ({ ...prev, search: newSearch }),
                replace: true,
            });
        },
    });

    const [isSearchOpen, setIsSearchOpen] = useState(!!searchField);

    useEffect(() => {
        if (isSearchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isSearchOpen]);

    function handleCreated(finalCircuitId: string) {
        navigate({
            to: '/projects/$projectVersionId/re-inspections/final-circuits/$finalCircuitId/information',
            params: {
                ...params,
                finalCircuitId,
            },
            search,
        });
    }

    return (
        <Page>
            <Section>
                <Section.Header>
                    <Section.Title>
                        {t('installation.layout.navigation.final-circuits.title')}
                    </Section.Title>
                    <Section.Header.Actions>
                        {isSearchOpen
                            ? (
                                    <Input
                                        ref={searchInputRef}
                                        type="search"
                                        className="w-64"
                                        placeholder={t('actions.search.search', { ns: 'common' })}
                                        leftSlot={<Search size={20} />}
                                        value={searchField ?? ''}
                                        onChange={e => setSearchField(e.target.value)}
                                        onBlur={() => setIsSearchOpen(false)}
                                    />
                                )
                            : (
                                    <Section.Header.Action
                                        icon={<Search size={20} />}
                                        onClick={() => setIsSearchOpen(true)}
                                    />
                                )}
                        <FinalCircuitCreateModal
                            electricalProjectVersionId={params.projectVersionId}
                            onCreated={handleCreated}
                            trigger={(
                                <Section.Header.Action
                                    variant="default"
                                    icon={<Plus size={20} />}
                                />
                            )}
                        />
                    </Section.Header.Actions>
                </Section.Header>
                <Section.Content>
                    <FinalCircuitList />
                </Section.Content>
                <Section.Footer>
                    <FooterNavigation />
                </Section.Footer>
            </Section>
        </Page>
    );
}

function FinalCircuitList() {
    const params = Route.useParams();
    const search = Route.useSearch();
    const { search: searchField } = search;

    const finalCircuits = useQuery(elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({
        electricalProjectVersionId: params.projectVersionId,
    }));

    const filteredFinalCircuits = useMemo(() => {
        const circuits = finalCircuits.data?.FinalCircuits ?? [];
        if (!searchField)
            return circuits;
        return circuits.filter(
            x => x.Identifier?.toLowerCase().includes(searchField.toLowerCase()),
        );
    }, [finalCircuits.data, searchField]);

    return (
        <FinalCircuitTable
            electricalProjectVersionId={params.projectVersionId}
            finalCircuits={filteredFinalCircuits}
            isLoading={finalCircuits.isPending}
            search={search}
        />
    );
}
